/**
 * Show, on the screen itself, why the venue failed to draw.
 *
 * A phone has no console. When the canvas comes up black on somebody's Android
 * there is no way to ask it what happened — and guessing from a desktop is how
 * you spend an hour ruling out things that were never wrong. This turns the
 * device into the instrument: load with `?diag=1` and anything that would have
 * gone to a console it does not have is printed over the page instead.
 *
 * It is opt-in for a reason. Nobody arriving at a memorial should meet a stack
 * trace, so without the flag none of this is installed and the venue behaves
 * exactly as it did before.
 *
 * Three things are watched, because a black canvas is usually one of them:
 *
 *   - an exception thrown while the scene was building, which stops it drawing
 *     but leaves the page and the HUD looking perfectly healthy
 *   - a rejected promise, which is how a failed asset load surfaces
 *   - WebGL context loss, which a phone does under memory pressure and which
 *     silently blanks everything already drawn
 */

import { describeQuality } from "./settings.js";

const FLAG = "diag";

let panel: HTMLElement | null = null;

function ensurePanel(): HTMLElement {
  if (panel) return panel;
  const el = document.createElement("div");
  el.setAttribute("role", "log");
  // Inline styles on purpose: the stylesheet is one of the things that may not
  // have loaded, and a diagnostic that depends on the thing it is diagnosing
  // is not a diagnostic.
  el.style.cssText = [
    "position:fixed",
    "inset:auto 0 0 0",
    "z-index:2147483647",
    "max-height:55vh",
    "overflow:auto",
    "margin:0",
    "padding:10px 12px",
    "background:#140406f2",
    "color:#ffd9d9",
    "font:12px/1.45 ui-monospace,Menlo,Consolas,monospace",
    "white-space:pre-wrap",
    "word-break:break-word",
    "border-top:2px solid #ff6b6b",
  ].join(";");
  el.textContent = "diagnostics on — anything below is what your device reported\n";
  document.body.appendChild(el);
  panel = el;
  return el;
}

function report(label: string, detail: string): void {
  ensurePanel().textContent += `\n[${label}] ${detail}\n`;
}

/** Everything useful an unknown thrown value might be carrying. */
function describe(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}\n${value.stack ?? ""}`;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Everything a throwaway canvas can say about this device's WebGL. */
function describeDevice(): string {
  const probe = document.createElement("canvas");
  const gl2 = probe.getContext("webgl2") as WebGL2RenderingContext | null;
  const gl = (gl2 ??
    probe.getContext("webgl") ??
    probe.getContext("experimental-webgl")) as WebGLRenderingContext | null;
  if (!gl) return `NO WebGL on this device at all · ${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}`;

  const info = gl.getExtension("WEBGL_debug_renderer_info");
  const renderer = info
    ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL))
    : String(gl.getParameter(gl.RENDERER));
  const vendor = info
    ? String(gl.getParameter(info.UNMASKED_VENDOR_WEBGL))
    : String(gl.getParameter(gl.VENDOR));

  return (
    `${renderer} · vendor ${vendor}` +
    ` · ${gl2 ? "WebGL2" : "WebGL1 only"}` +
    ` · max texture ${gl.getParameter(gl.MAX_TEXTURE_SIZE)}px` +
    ` · texture units ${gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS)}` +
    ` · varyings ${gl.getParameter(gl.MAX_VARYING_VECTORS)}` +
    ` · vertex attribs ${gl.getParameter(gl.MAX_VERTEX_ATTRIBS)}` +
    ` · ${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}`
  );
}

export function installDiagnostics(): void {
  if (new URLSearchParams(window.location.search).get(FLAG) !== "1") return;

  window.addEventListener("error", (event) => {
    // A failed <img>/<script> fires error on the element, not the window, and
    // arrives with no message — which is exactly the case worth seeing.
    const target = event.target as (HTMLElement & { src?: string }) | null;
    if (target && target !== (window as unknown as HTMLElement) && target.src) {
      report("asset failed", target.src);
      return;
    }
    report("error", describe(event.error ?? event.message));
  });

  window.addEventListener("unhandledrejection", (event) => {
    report("promise rejected", describe(event.reason));
  });

  /*
   * The canvas does not exist yet when this runs — Phaser makes it later — so
   * the listeners are attached once it appears rather than on a timer.
   */
  const watchCanvas = (canvas: HTMLCanvasElement): void => {
    canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      report("WEBGL CONTEXT LOST", "the GPU dropped the canvas — this blanks everything");
    });
    canvas.addEventListener("webglcontextcreationerror", (event) => {
      report("WEBGL WOULD NOT START", (event as { statusMessage?: string }).statusMessage ?? "");
    });
    /*
     * `webgl` FIRST, and the order is the whole point.
     *
     * Asking a canvas for a context type it does not already have fires
     * `webglcontextcreationerror` — so probing webgl2 on a canvas Phaser had
     * already given a WebGL1 context made this panel report "WEBGL WOULD NOT
     * START" on a device whose WebGL was working perfectly, and then print the
     * renderer two lines later. A diagnostic that invents the fault it is
     * there to find is worse than no diagnostic.
     *
     * WebGL1 is what Phaser takes on most phones, so it is asked for first and
     * webgl2 is only tried when there is no context to collide with.
     */
    const gl = canvas.getContext("webgl") ?? canvas.getContext("webgl2");
    const info = gl?.getExtension("WEBGL_debug_renderer_info");
    // A fresh canvas tells us whether the DEVICE can do WebGL at all, which
    // separates "this phone cannot" from "Phaser's canvas specifically failed".
    const probe = document.createElement("canvas");
    const deviceCan = Boolean(probe.getContext("webgl2") ?? probe.getContext("webgl"));

    report(
      "renderer",
      (gl
        ? `${info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.VENDOR)} · ` +
          `max texture ${gl.getParameter(gl.MAX_TEXTURE_SIZE)}px`
        : "game canvas has NO WebGL context — Phaser fell back to 2D") +
        ` · device supports WebGL: ${deviceCan}` +
        ` · ${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}` +
        ` · canvas ${canvas.width}x${canvas.height}`,
    );
  };

  /*
   * Phaser's canvas, specifically.
   *
   * `document.querySelector("canvas")` is wrong here and wrong in a way that
   * lies convincingly: the gate draws each avatar into its own 2D canvas, so
   * the first canvas in the document is a character preview, and asking a 2D
   * canvas for a WebGL context correctly returns nothing. That reads as "the
   * venue has no WebGL" when the venue has not been looked at.
   *
   * Phaser mounts inside #game-root, so that is what gets inspected.
   */
  const findGameCanvas = (): HTMLCanvasElement | null =>
    document.querySelector<HTMLCanvasElement>("#game-root canvas");

  // The quality mode, which decides whether the venue draws its particles,
  // flicker and full diya count at all. A venue that "looks basic" with a
  // healthy renderer is almost always this and not the GPU.
  report("quality", describeQuality());

  /*
   * What this DEVICE can do, asked immediately and without Phaser.
   *
   * The renderer line below needs the game canvas, which does not exist until
   * somebody has gone through the gate — so the first person asked for a
   * report sent back the quality line alone, from the join screen, and the
   * half that mattered was missing. This half needs nothing but a throwaway
   * canvas, so it goes out at once and the reader gets the GPU either way.
   *
   * The texture unit count is here because it is the number that decides
   * whether Phaser's multi-texture batching is safe on this driver, and a
   * batching fault is what corrupted geometry looks like.
   */
  report("device", describeDevice());

  const existing = findGameCanvas();
  if (existing) {
    watchCanvas(existing);
    return;
  }
  const observer = new MutationObserver(() => {
    const canvas = findGameCanvas();
    if (!canvas) return;
    observer.disconnect();
    window.clearTimeout(giveUp);
    watchCanvas(canvas);
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Silence is the one result a reader cannot act on: "no renderer line" reads
  // the same whether the venue never opened or the report was copied early.
  const giveUp = window.setTimeout(() => {
    observer.disconnect();
    if (!findGameCanvas()) {
      report("renderer", "no game canvas yet — enter the venue, then read this again");
    }
  }, 20_000);
}
